import * as React from 'react'
import { Check, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { WeekDay } from '@/types/booking'

interface CopyDaysPopupProps {
  isOpen: boolean
  onClose: (open?: boolean) => void
  onApply: (selectedDays: WeekDay[]) => void
  sourceDay: WeekDay
  allDays: { day: WeekDay; label: string }[]
}

export function CopyDaysPopup({
  isOpen,
  onClose,
  onApply,
  sourceDay,
  allDays
}: CopyDaysPopupProps) {
  const [selectedDays, setSelectedDays] = React.useState<WeekDay[]>([])
  const [isSuccess, setIsSuccess] = React.useState(false)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const selectedRef = React.useRef<HTMLButtonElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (isOpen && selectedRef.current && listRef.current) {
      selectedRef.current.scrollIntoView({ block: 'center' })
    }
  }, [isOpen])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  const handleDayToggle = (day: WeekDay) => {
    if (day === sourceDay) return // Can't toggle source day
    
    setSelectedDays(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day)
      } else {
        return [...prev, day]
      }
    })
  }

  const handleApply = () => {
    if (selectedDays.length === 0) return
    setIsSuccess(true)
    setTimeout(() => {
      onApply(selectedDays)
      setIsSuccess(false)
      setSelectedDays([])
    }, 500)
  }

  return (
    <div ref={rootRef} className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => isOpen ? onClose() : onClose(!isOpen)}
        className="h-8 w-8 p-0"
      >
        <Copy className="h-4 w-4" />
      </Button>

      {isOpen && (
        <div
          ref={listRef}
          className="absolute z-50 top-0 left-[calc(100%+4px)] w-48 rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          <div className="p-2 space-y-1 max-h-[280px] overflow-auto">
            {allDays.map(({ day, label }) => (
              <button
                key={day}
                ref={day === sourceDay ? selectedRef : null}
                onClick={() => handleDayToggle(day)}
                disabled={day === sourceDay}
                className={cn(
                  "relative w-full flex items-center py-1.5 px-2 text-sm rounded-sm outline-none transition-colors",
                  day === sourceDay && "bg-muted text-muted-foreground",
                  selectedDays.includes(day) && "bg-primary text-primary-foreground",
                  !selectedDays.includes(day) && day !== sourceDay && "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <div className="flex-1 text-left">{label}</div>
                {(selectedDays.includes(day) || day === sourceDay) && (
                  <Check 
                    className={cn(
                      "h-4 w-4 ml-2",
                      day === sourceDay ? "text-muted-foreground" : "text-white"
                    )} 
                  />
                )}
              </button>
            ))}
          </div>

          <div className="border-t p-2">
            <Button
              size="sm"
              onClick={handleApply}
              disabled={selectedDays.length === 0 || isSuccess}
              className="w-full relative"
            >
              {isSuccess ? "Applied!" : "Apply"}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}